import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const { login, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const u = res.user;
      let dest = location.state?.from;
      if (!dest) {
        if (u?.role === "admin") dest = "/admin";
        else if (u?.role === "etchery") dest = "/admin";
        else if (u?.role === "subscriber" || (u?.roles || []).includes("subscriber")) dest = "/subscriber";
        else if (u?.role === "grower" && u?.application_status === "active") dest = "/grower";
        else dest = "/portal";
      }
      navigate(dest, { replace: true });
    } else {
      setErr(res.error);
    }
  };

  if (user) {
    let destPath = "/portal";
    let destLabel = "Portal";
    if (user.role === "admin") { destPath = "/admin"; destLabel = "Console"; }
    else if (user.role === "subscriber" || (user.roles || []).includes("subscriber")) { destPath = "/subscriber"; destLabel = "My Subscription"; }
    else if (user.role === "grower" && user.application_status === "active") { destPath = "/grower"; destLabel = "Grower Portal"; }
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="login-already-signed-in">
        <div className="bloom-card p-10 max-w-md text-center">
          <div className="bloom-label mb-3">Already signed in</div>
          <h2 className="font-serif text-3xl mb-3">You&rsquo;re signed in as {user.name}.</h2>
          <div className="flex gap-3 justify-center flex-wrap">
            <button onClick={() => navigate(destPath)} className="bloom-btn bloom-btn-primary">
              Continue to {destLabel}
            </button>
            <button onClick={async () => { await logout(); }} className="bloom-btn bloom-btn-outline">
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-stretch" data-testid="login-page">
      {/* Left: editorial side */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden bg-[var(--bloom-ink)] text-[var(--bloom-bg)] p-16 flex-col justify-between">
        <div className="relative z-10">
          <div className="bloom-seal" style={{background: "var(--bloom-bg)", color: "var(--bloom-ink)"}}>HBC<br/>2026</div>
          <div className="mt-12 max-w-md">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-6">✦ Founding Grower Programme ✦</div>
            <h1 className="font-serif text-5xl leading-[1.05]">
              The next step <em>in joining</em> our grower network.
            </h1>
            <p className="mt-8 font-serif italic text-lg opacity-70 leading-relaxed">
              &ldquo;A bloom is a quiet act of belief. We are looking for those who still believe.&rdquo;
            </p>
          </div>
        </div>
        <div className="relative z-10 text-xs tracking-[0.2em] uppercase opacity-50">
          Heritage Dahlias · Aotearoa
        </div>
        <div className="bloom-grain" aria-hidden />
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-16 bg-[var(--bloom-bg)]">
        <div className="w-full max-w-sm bloom-rise">
          <div className="bloom-divider mb-10">✦ Stage Two ✦</div>
          <h2 className="font-serif text-4xl mb-2">Welcome back</h2>
          <p className="text-sm text-[var(--bloom-ink-soft)] mb-10">Continue your review of the Bloom Club prospectus.</p>

          <form onSubmit={submit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="bloom-label block mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bloom-input"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="bloom-label block mb-1">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bloom-input"
                data-testid="login-password-input"
              />
            </div>

            {err && <div className="text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="login-error">{err}</div>}

            <button type="submit" disabled={loading} className="bloom-btn bloom-btn-primary w-full" data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Continue"}
            </button>
          </form>

          <div className="mt-10 text-sm text-[var(--bloom-ink-soft)] text-center">
            New to the programme?{" "}
            <Link to="/register" className="underline decoration-[var(--bloom-brand)] underline-offset-4" data-testid="login-register-link">
              Create your prospectus account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
